import { parseToDateTime } from '@calendly/datetime'
import { I18n } from '@calendly/i18n'
import { nanoid } from 'nanoid'

import { MESSAGE_ROLES, TOOL_CALL_NAME_MAPPING, LINK_BADGE_MAX_TEXT_LENGTH } from './constants'

// Luxon `a` may emit lowercase am/pm (ICU/locale); Moment's `A` was uppercase AM/PM.
const withUppercaseMeridiem = (formatted) => formatted.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())

const createMessage = ({ role, content, status, queryId, responseId = null }) => ({
  id: nanoid(5),
  role,
  content,
  status,
  queryId,
  responseId
})

export const createUserMessage = ({ content, status, queryId = null }) => createMessage({
  role: MESSAGE_ROLES.USER,
  content,
  status,
  queryId
})
export const createAssistantMessage = ({ content, status, queryId, responseId = null }) => createMessage({
  role: MESSAGE_ROLES.ASSISTANT,
  content,
  status,
  queryId,
  responseId
})

// Helper function for case-insensitive message role comparisons
const isMessageRole = (role, targetRole) => {
  if (!role || !targetRole) return false
  return role.toUpperCase() === targetRole.toUpperCase()
}

// Convenience functions for common role checks
export const isUserMessage = (message) => isMessageRole(message?.role, MESSAGE_ROLES.USER)
export const isAssistantMessage = (message) => isMessageRole(message?.role, MESSAGE_ROLES.ASSISTANT)

export const getFeedbackOptions = () => [
  { id: 1, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.not_factual') },
  { id: 2, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.cant_access_data') },
  { id: 3, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.off_topic') },
  { id: 4, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.too_long') },
  { id: 5, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.inappropriate_tone') },
  { id: 6, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.hard_to_understand') },
  { id: 7, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.bad_formatting') },
  { id: 8, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.too_slow') },
  { id: 9, name: I18n.t('publisher.agent_hub.feedback_popup.reasons.other') }
]

/**
 * Get the translated message for a tool call name
 * @param {string} toolCallName - The name of the tool call
 * @returns {string} The translated message, or null if no mapping exists
 */
// i18n-tasks-use t('publisher.agent_hub.tool_calls.get_invitees')
// i18n-tasks-use t('publisher.agent_hub.tool_calls.list_contacts')
// i18n-tasks-use t('publisher.agent_hub.tool_calls.get_user_and_meetings_in_time_range_optionally_filter_by_emails')
export const getToolCallMessage = (toolCallName) => {
  if (!toolCallName || !TOOL_CALL_NAME_MAPPING[toolCallName]) {
    return null
  }
  // The toolCallName is validated against TOOL_CALL_NAME_MAPPING above
  return I18n.t(`publisher.agent_hub.tool_calls.${toolCallName}`)
}

/**
 * Truncate text to a maximum length and append ellipsis if needed.
 * Uses Array.from() to properly handle Unicode characters (including emojis
 * and international characters that use surrogate pairs in UTF-16).
 * @param {string} text - The text to truncate
 * @param {number} [maxLength=LINK_BADGE_MAX_TEXT_LENGTH] - Maximum length in Unicode codepoints before truncation
 * @returns {string} The truncated text with ellipsis, or empty string if input is invalid/empty
 */
export const truncateText = (text, maxLength = LINK_BADGE_MAX_TEXT_LENGTH) => {
  if (typeof text !== 'string') return ''

  const trimmedText = text.trim()
  if (!trimmedText) return ''

  const characters = Array.from(trimmedText)
  if (characters.length > maxLength) {
    return `${characters.slice(0, maxLength).join('')}...`
  }
  return trimmedText
}


/**
 * Extracts all attendee names/emails from GraphQL attendees array
 * Preserves the order from the GQL response
 * @param {Array} attendees - Array of attendee objects from GraphQL
 * @returns {string[]} Array of attendee names or emails
 */
export const getAttendeeNames = (attendees) => {
  if (!attendees || !Array.isArray(attendees)) return []

  return attendees
    .map((a) => a.name || a.email || '')
    .filter(Boolean)
}

/**
 * Formats an array of attendee names to fit within a container,
 * truncating with "+ X more" if needed.
 *
 * @param {string[]} attendees - Array of attendee names/emails
 * @param {number} maxChars - Maximum characters to display before truncating
 * @returns {{ displayText: string, remainingCount: number }}
 */
export const formatAttendeesList = (attendees, maxChars = 40) => {
  if (!attendees || attendees.length === 0) {
    return { displayText: '', remainingCount: 0 }
  }

  if (attendees.length === 1) {
    return { displayText: attendees[0], remainingCount: 0 }
  }

  let displayText = ''
  let includedCount = 0
  const separator = ', '

  for (let i = 0; i < attendees.length; i += 1) {
    const name = attendees[i]
    const potentialText = displayText
      ? `${displayText}${separator}${name}`
      : name

    // Check if adding this name would exceed the limit
    // Account for the "+ X more" suffix that might be needed
    const remainingAfterThis = attendees.length - (i + 1)
    const suffixLength = remainingAfterThis > 0
      ? `${I18n.t('publisher.agent_hub.recap_hover_card.attendees_more', { count: remainingAfterThis })}`.length
      : 0

    if (potentialText.length + suffixLength <= maxChars || i === 0) {
      displayText = potentialText
      includedCount = i + 1
    } else {
      break
    }
  }

  const remainingCount = attendees.length - includedCount
  return { displayText, remainingCount }
}

/**
 * Formats start and end times into a readable datetime string
 * Start time includes AM/PM only when it differs from the end time's period
 * @param {string|null|undefined} startTime - The start time (ISO string or date-like)
 * @param {string|null|undefined} endTime - The end time (ISO string or date-like)
 * @returns {string} Formatted datetime string like "Mon, January 1, 2024, 2:00 - 3:00 PM"
 *   or "Mon, January 1, 2024, 11:00 AM - 1:00 PM" or empty string
 */
export const getFormattedDateTime = (startTime, endTime) => {
  const startDt = startTime ? parseToDateTime(startTime) : null
  const endDt = endTime ? parseToDateTime(endTime) : null

  const dateFormatted = startDt?.isValid
    ? startDt.toFormat('ccc, MMMM d, yyyy')
    : ''

  const timeFormatted = startDt?.isValid && endDt?.isValid
    ? (() => {
      const startMer = startDt.toFormat('a').toLowerCase()
      const endMer = endDt.toFormat('a').toLowerCase()
      const startFmt = startMer === endMer ? 'h:mm' : 'h:mm a'
      return `${withUppercaseMeridiem(startDt.toFormat(startFmt))} - ${withUppercaseMeridiem(endDt.toFormat('h:mm a'))}`
    })()
    : ''

  if (dateFormatted && timeFormatted) {
    return `${dateFormatted}, ${timeFormatted}`
  }

  return ''
}
